import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Calendar,
  Mail,
  MessageSquare,
  Phone,
  CheckCircle,
  FileText,
  Activity,
  User,
  RefreshCw,
  List,
  Table as TableIcon,
  ChevronDown,
  ChevronUp,
  Zap,
  MoreVertical,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowUpDown,
  Settings
} from 'lucide-react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import SubHeader from '../../components/common/SubHeader/SubHeader';
import { getSession } from '../../getSession';
import usePermissions from '../Master_Settings/usePermissions';
import useFieldMasking from '../Master_Settings/useFieldMasking';
import API_ENDPOINTS from '../../config/apiEndpoints';
import PremiumModuleCards from '../Task/Components/common/PremiumModuleCards';
import { LeadDetails } from '../../components/common/LeadDetails/LeadDetails';
import DynamicCalendar from '../../components/DynamicCalendar/DynamicCalendar';
import Loader from '../../components/Loader/Loader';
import nodata from '../../assets/nodata.gif';
import { createPortal } from 'react-dom';
import './LeadActivityModule.css';

import LeadActivityListTable from './LeadActivityListTable';

// Centralized API Base for Metadata Hover
const API_BASE_SERVICES = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const GET_ACTIVITY_META_DATA = `${API_BASE_SERVICES}${API_ENDPOINTS.LEAD_ACTIVITIES.GET_LEAD_DETAILS_ACTIVITY_DATA}`;

/**
 * ActivityMetaHover Component
 * A hover diagnostic popup that fetches and displays activity metadata.
 */
export const ActivityMetaHover = ({ activity, children }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [metaData, setMetaData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [coords, setCoords] = React.useState({ top: 0, left: 0 });

  const fetchMetaData = async () => {
    setIsLoading(true);
    try {
      const session = getSession();
      const payload = {
        Token: session.TokenId,
        Details: {
          ParentId: session.parentId
        }
      };

      const response = await fetch(GET_ACTIVITY_META_DATA, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.Details;
        if (data) {
          setMetaData(Array.isArray(data) ? data[0] : data);
        }
      }
    } catch (error) {
      console.error("Error fetching activity metadata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (isHovered && !metaData && !isLoading) {
      fetchMetaData();
    }
  }, [isHovered]);

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCoords({
      top: rect.top + window.scrollY,
      left: rect.right + window.scrollX + 15
    });
    setIsHovered(true);
  };

  return (
    <>
      <div
        className="la-activity-icon-hover-trigger"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {children}
      </div>
      {isHovered && createPortal(
        <div
          className="activity-metadata-popover la-animate-fade-in"
          style={{
            position: 'absolute',
            top: coords.top,
            left: coords.left,
            zIndex: 9999,
            marginTop: '-20px'
          }}
        >
          <div className="metadata-card-modern">
            <div className="metadata-card-header">Activity Meta Details</div>
            <div className="metadata-card-body">
              {isLoading ? (
                <div className="loading-mini">
                  <div className="la-mini-spinner"></div>
                  <span>Fetching metadata...</span>
                </div>
              ) : metaData ? (
                <div className="metadata-grid-mini">
                  {Object.entries(metaData).map(([key, value]) => {
                    // Filter out internal IDs or empty values
                    if (key === 'Id' || key === 'ActivityId' || !value || value === '0' || value === '0.00' || value === 'null') return null;
                    return (
                      <div key={key} className="metadata-mini-row">
                        <span className="mini-label">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="mini-value">{String(value)}</span>
                      </div>
                    );
                  })}
                  {Object.keys(metaData || {}).length === 0 && (
                    <div className="no-data-mini">No details found for this activity</div>
                  )}
                </div>
              ) : (
                <div className="no-data-mini">No details found for this activity</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export const getAvatarStyle = (name) => {
  const themes = [
    { bg: '#eff6ff', color: '#1d4ed8' }, // Blue
    { bg: '#ecfdf5', color: '#059669' }, // Green
    { bg: '#fef2f2', color: '#dc2626' }, // Red
    { bg: '#fff7ed', color: '#ea580c' }, // Orange
    { bg: '#faf5ff', color: '#7e22ce' }, // Purple
    { bg: '#fdf2f8', color: '#db2777' }, // Pink
    { bg: '#f0fdf4', color: '#16a34a' }, // Emerald
    { bg: '#fffbeb', color: '#d97706' }, // Amber
    { bg: '#f5f3ff', color: '#8b5cf6' }, // Violet
    { bg: '#f0f9ff', color: '#0284c7' }, // Sky
    { bg: '#fdf4ff', color: '#a21caf' }, // Fuchsia
    { bg: '#fff1f2', color: '#e11d48' }, // Rose
  ];

  if (!name || name === 'N/A' || name === 'null') return themes[0];

  // Use a simple hash to get a more consistent but varied index
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % themes.length;
  return themes[index];
};

export const getInitials = (name) => {
  if (!name || name === 'N/A' || name === 'null') return '??';

  const digitsOnly = name.toString().replace(/\D/g, '');
  if (digitsOnly.length >= 2 && (/^\+?\d+/.test(name.trim()) || digitsOnly.length === name.trim().replace(/\s/g, '').length)) {
    return digitsOnly.substring(0, 2);
  }

  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '??';
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const getBadgeClass = (eventName) => {
  const name = (eventName || '').toLowerCase();
  if (name.includes('whatsapp')) return 'badge-whatsapp';
  if (name.includes('email') || name.includes('mail')) return 'badge-email';
  if (name.includes('sms')) return 'badge-sms';
  return 'badge-default';
};

export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  // If it's already DD-MMM-YYYY, return as is
  if (/^\d{2}-[a-zA-Z]{3}-\d{4}$/.test(dateStr)) return dateStr;

  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

export const formatActivityTime = (timeStr) => {
  if (!timeStr) return '00:00am';

  try {
    // Handle HH:mm:ss am/pm format (Strip seconds and spaces)
    const match = timeStr.trim().match(/^(\d{2}):(\d{2}):(\d{2})\s*([ap]m)$/i);
    if (match) {
      const [_, h, m, s, ampm] = match;
      return `${parseInt(h, 10)}:${m}${ampm.toLowerCase()}`;
    }

    // Handle HH:mm format
    if (/^\d{2}:\d{2}$/.test(timeStr)) {
      const [h, m] = timeStr.split(':');
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m}${ampm}`;
    }
    return timeStr.toLowerCase().replace(/\s+/g, '');
  } catch {
    return timeStr;
  }
};

export const getRemoteIcon = (eventName) => {
  const baseURL = "https://kit19.com/assets/custom/UserCRMCampaign/Images/LeadActivityIMG/";
  const name = (eventName || '').toLowerCase();
  let iconName = 'lifecycle.png'; // Default

  if (name.includes('sms')) iconName = 'sms.png';
  else if (name.includes('email') || name.includes('mail')) iconName = 'email.png';
  else if (name.includes('whatsapp')) iconName = 'whatsapp.png';
  else if (name.includes('call') || name.includes('voice')) iconName = 'voice_calls.png';
  else if (name.includes('appointment')) iconName = 'appointment.png';
  else if (name.includes('task')) iconName = 'task.png';
  else if (name.includes('webform')) iconName = 'webform.png';
  else if (name.includes('lifecycle')) iconName = 'lifecycle.png';
  else if (name.includes('support')) iconName = 'support.png';
  else if (name.includes('notes')) iconName = 'notes.png';

  return `${baseURL}${iconName}`;
};

const LeadActivityModule = () => {
  const { parentId, TokenId } = getSession();

  const { permissions } = usePermissions('PAG10037');
  const { maskObject, isHiddenField } = useFieldMasking('PAG10372#PAG10037');
  const activityPerms = useMemo(() => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find(p => p.ModuleCode?.includes('PAG10037')) || {};
  }, [permissions]);

  // Today Count State
  const [todayActivitiesCount, setTodayActivitiesCount] = useState(0);

  // Date Helpers
  const formatDateForPayload = (date) => {
    if (!date) return '';
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      // If it's already in DD-MMM-YYYY format, return as is
      if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(date)) return date;
      return '';
    }
    return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  const formatDisplayDateV2 = (dateStr) => {
    if (!dateStr) return 'Select Date';
    return dateStr; // Since our internal state is already DD-MMM-YYYY
  };


  const formatForDateInput = (dateStr) => {
    if (!dateStr) return '';
    const months = { "Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04", "May": "05", "Jun": "06", "Jul": "07", "Aug": "08", "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12" };

    // Check if it's DD-MMM-YYYY
    if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('-');
      const day = parts[0];
      const month = months[parts[1]] || '01';
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }

    // Fallback for raw date string
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const parseActivityDate = (dateStr, timeStr = "") => {
    if (!dateStr || dateStr === "0" || dateStr === "null") return null;
    try {
      const months = {
        'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
        'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
      };

      const normalizedDate = dateStr.trim().toLowerCase();
      const dateParts = normalizedDate.split(' ');

      // 1. Handle "Apr 13 2026" format (Provided by USER)
      if (dateParts.length === 3 && months.hasOwnProperty(dateParts[0])) {
        const month = months[dateParts[0]];
        const day = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);

        let hours = 0, minutes = 0;
        if (timeStr) {
          const timeMatch = timeStr.trim().match(/^(\d+):(\d+)(am|pm)$/i);
          if (timeMatch) {
            hours = parseInt(timeMatch[1], 10);
            minutes = parseInt(timeMatch[2], 10);
            const ampm = timeMatch[3].toLowerCase();
            if (ampm === 'pm' && hours < 12) hours += 12;
            if (ampm === 'am' && hours === 12) hours = 0;
          }
        }

        const d = new Date(year, month, day, hours, minutes);
        if (!isNaN(d.getTime())) return d;
      }

      // 2. Handle standard "DD-MMM-YYYY" or "YYYY-MM-DD" used in fallback
      let fallbackParts = normalizedDate.split(/-|\//);
      if (fallbackParts.length === 3) {
        let day, month, year;
        if (fallbackParts[0].length === 4) {
          year = parseInt(fallbackParts[0], 10);
          month = isNaN(fallbackParts[1]) ? months[fallbackParts[1]] : months[fallbackParts[1]] || parseInt(fallbackParts[1], 10) - 1;
          day = parseInt(fallbackParts[2], 10);
        } else {
          day = parseInt(fallbackParts[0], 10);
          month = isNaN(fallbackParts[1]) ? months[fallbackParts[1]] : months[fallbackParts[1]] || parseInt(fallbackParts[1], 10) - 1;
          year = parseInt(fallbackParts[2], 10);
        }

        if (!isNaN(day) && month !== undefined && !isNaN(year)) {
          const d = new Date(year, month, day);
          if (!isNaN(d.getTime())) return d;
        }
      }

      // 3. Fallback to native parsing
      const fallback = new Date(dateStr + (timeStr ? " " + timeStr : ""));
      if (!isNaN(fallback.getTime())) return fallback;

      return null;
    } catch (e) {
      console.error("Date parsing error:", e, dateStr, timeStr);
      return null;
    }
  };

  // State Management
  const [activities, setActivities] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('table'); // Default to table as requested
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [jumpPage, setJumpPage] = useState(1); // Local state for input typing
  const [viewingLeadId, setViewingLeadId] = useState(null); // Track selected lead for details
  const [viewingActivity, setViewingActivity] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'EventDate', direction: 'desc' });
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false); // Restore filter panel state
  const [leadSuggestions, setLeadSuggestions] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [leadSearchText, setLeadSearchText] = useState('');

  // Date Range State
  const [fromDate, setFromDate] = useState(formatDateForPayload(new Date(new Date().getFullYear(), 0, 1)));
  const [toDate, setToDate] = useState(formatDateForPayload(new Date()));

  // Activity Type Toggles
  const [filters, setFilters] = useState({
    bl_Lifecycle: true, bl_Appointment: true, bl_Docs: true, bl_Email: true,
    bl_Eshop: true, bl_LandingPage: true, bl_Notes: true, bl_SMS: true,
    bl_Support: true, bl_Task: true, bl_Voice_Calls: true, bl_WebForm: true
  });

  // Temporary states for filter panel (Sync to main states on Apply)
  const [tempFilters, setTempFilters] = useState(filters);
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const [tempLeadSearchText, setTempLeadSearchText] = useState('');
  const [tempSelectedLeadId, setTempSelectedLeadId] = useState('');
  const [filterLogic, setFilterLogic] = useState('OR');
  const [tempFilterLogic, setTempFilterLogic] = useState('OR');


  // Sync temp states when filter panel opens
  useEffect(() => {
    if (isFilterOpen) {
      setTempFilters({ ...filters });
      setTempFromDate(fromDate);
      setTempToDate(toDate);
      setTempLeadSearchText(leadSearchText);
      setTempSelectedLeadId(selectedLeadId);
      setTempFilterLogic(filterLogic);
    }
  }, [isFilterOpen]);


  const activityFields = [
    { label: 'Activity Type', value: 'EventName' },
    { label: 'Customer Name', value: 'PersonName' },
    { label: 'Mobile No', value: 'MobileNo' },
    { label: 'Owner', value: 'UserName' },
    { label: 'Date', value: 'EventDate', type: 'date' },
    { label: 'Enquiry ID', value: 'EnquiryID' }
  ];

  const handleAdvancedFilterSubmit = (newFilters) => {
    setAdvancedFilters(newFilters);
    setPageNumber(1);
  };

  const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
  const FOR_LEAD_SEARCH = `${NEWV3_BASE_URL}/${API_ENDPOINTS.TASKS.RELATED_TO_SUGGETION}`;

  // Lead Suggestions Fetching
  useEffect(() => {
    if (!leadSearchText || leadSearchText.length < 1) {
      setLeadSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const payload = {
          Token: "-2295521862261168",
          Details: {
            TaskType: "Lead",
            FilterText: leadSearchText,
            UserId: parseInt(parentId)
          }
        };

        const response = await fetch(FOR_LEAD_SEARCH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const result = await response.json();
          let suggestions = result.d || [];

          // Some WCF services return stringified JSON in 'd'
          if (typeof suggestions === 'string') {
            try {
              suggestions = JSON.parse(suggestions);
            } catch (e) {
              suggestions = [];
            }
          }

          setLeadSuggestions(Array.isArray(suggestions) ? suggestions : []);
        }
      } catch (err) {
        console.error("Lead suggestion error:", err);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [leadSearchText, parentId, FOR_LEAD_SEARCH]);

  const [activeStat, setActiveStat] = useState('All'); // New state for scorecard filtering


  // Handle Scorecard Clicks
  const handleStatusClick = (key) => {
    if (key === 'ResetAll') {
      handleShowAll();
      return;
    }
    if (key === 'Summary') return; // Top Records is informative only

    setActiveStat(key);
    if (key === 'Leads') {
      setPageSize(2000);
      setPageNumber(1);
    } else {
      setPageSize(20);
      setPageNumber(1);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredActivities = useMemo(() => {
    if (!searchText) return activities;
    const q = searchText.toLowerCase();
    return activities.filter(act => {
      const personName = (act.PersonName || '').toLowerCase();
      const mobileNo = (act.Mobileno || act.MobileNo || act.Mobile || '').toLowerCase();
      const userName = (act.UserName || '').toLowerCase();
      const eventName = (act.EventName || '').toLowerCase();
      const eventDesc = (act.EventDescription || '').toLowerCase();
      const enquiryId = (act.EnquiryID || act.EnquiryId || act.LeadID || '').toString().toLowerCase();

      return personName.includes(q) ||
        mobileNo.includes(q) ||
        userName.includes(q) ||
        eventName.includes(q) ||
        eventDesc.includes(q) ||
        enquiryId.includes(q);
    });
  }, [activities, searchText]);

  const sortedActivities = useMemo(() => {
    if (!sortConfig.key) return filteredActivities;

    // Create a copy to avoid mutating original state
    const data = [...filteredActivities];

    data.sort((a, b) => {
      let valA, valB;

      // Map display keys to actual data keys
      switch (sortConfig.key) {
        case 'details': valA = a.EventName || ''; valB = b.EventName || ''; break;
        case 'customer': valA = a.PersonName || ''; valB = b.PersonName || ''; break;
        case 'owner': valA = a.UserName || ''; valB = b.UserName || ''; break;
        case 'EventDate': valA = a.EventDate; valB = b.EventDate; break;
        default: valA = a[sortConfig.key] || ''; valB = b[sortConfig.key] || '';
      }

      // Special handling for dates
      if (sortConfig.key === 'EventDate' || sortConfig.key === 'date') {
        const dateA = new Date(valA);
        const dateB = new Date(valB);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // String comparison
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();

      if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [filteredActivities, sortConfig]);

  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleTempFilter = (key) => setTempFilters(prev => ({ ...prev, [key]: !prev[key] }));

  const handleFilterSelectAll = (checked) => {
    const newFilters = {};
    Object.keys(filters).forEach(key => newFilters[key] = checked);
    setFilters(newFilters);
  };

  const handleTempFilterSelectAll = (checked) => {
    const newFilters = {};
    Object.keys(tempFilters).forEach(key => newFilters[key] = checked);
    setTempFilters(newFilters);
  };


  // State for header animations/toolbar
  const [showViewOptions, setShowViewOptions] = useState(false);

  // API Fetching
  const fetchActivities = async () => {
    setIsLoading(true);
    try {
      const todayStr = formatDateForPayload(new Date());
      const payload = {
        Token: TokenId,
        Details: {
          strFromDate: activeStat === 'Today' ? todayStr : fromDate,
          strToDate: activeStat === 'Today' ? todayStr : toDate,
          strLeadName: leadSearchText,
          strLeadID: selectedLeadId || "",
          ParentId: parentId.toString(),
          ...filters
        }
      };

      const response = await fetch(`${process.env.REACT_APP_SERVICES_API_BASE_URL}${API_ENDPOINTS.LEAD_ACTIVITIES.GET_LEAD_ACTIVITY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${TokenId}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        let data = result.Details || [];

        // Apply Client-Side Filtering for Stats Cards
        if (activeStat === 'WhatsApp') {
          data = data.filter(a => (a.EventName || '').toLowerCase().includes('whatsapp'));
        } else if (activeStat === 'Leads') {
          // Keep only the first occurrence for each lead to show unique leads
          const seenLeads = new Set();
          data = data.filter(a => {
            const leadKey = a.LeadID || a.PersonName;
            if (seenLeads.has(leadKey)) return false;
            seenLeads.add(leadKey);
            return true;
          });
        }

        setActivities(data);

        if (activeStat === 'All') {
          setTotalRecords(data.length);
        }
      }
    } catch (error) {
      console.error("Error fetching lead activities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Separate effect to get Today's Count accurately (Dynamic based on current filters)
  useEffect(() => {
    const fetchTodayCount = async () => {
      try {
        const todayStr = formatDateForPayload(new Date());
        const payload = {
          Token: TokenId,
          Details: {
            strFromDate: todayStr,
            strToDate: todayStr,
            strLeadName: "",
            strLeadID: "",
            ParentId: parentId.toString(),
            ...filters
          }
        };

        const response = await fetch(`${process.env.REACT_APP_SERVICES_API_BASE_URL}${API_ENDPOINTS.LEAD_ACTIVITIES.GET_LEAD_ACTIVITY}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${TokenId}`
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const result = await response.json();
          const data = result.Details || [];
          setTodayActivities(data);
          // Use data.length for a dynamic count that matches the records found
          setTodayActivitiesCount(data.length);
        }
      } catch (error) {
        console.error("Error fetching today count:", error);
      }
    };

    fetchTodayCount();
  }, [parentId, TokenId]); // Fetch once on mount or when account changes

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchActivities();
    }, 400); // 400ms debounce for search and filter changes
    return () => clearTimeout(timer);
  }, [filters, pageNumber, pageSize, viewMode, searchText, fromDate, toDate, advancedFilters, activeStat, filterLogic, leadSearchText, selectedLeadId]);

  const totalPages = Math.ceil(totalRecords / pageSize);

  // Map activities to Calendar Events
  const calendarEvents = useMemo(() => {
    return activities.map((act, idx) => ({
      id: idx,
      title: act.EventName || 'Activity',
      date: parseActivityDate(act.EventDate, act.EventTime),
      status: (act.EventName || '').toLowerCase().includes('whatsapp') ? 'info' :
        (act.EventName || '').toLowerCase().includes('email') ? 'success' : 'open',
      type: act.EventName,
      extendedProps: act
    })).filter(e => e.date);
  }, [activities]);

  // Data-driven focus for the calendar
  const calendarInitialDate = useMemo(() => {
    if (calendarEvents.length > 0) {
      // Return the date of the first event so calendar jumps there
      return calendarEvents[0].date;
    }
    return new Date();
  }, [calendarEvents]);

  // Sync local jumpPage input with actual pageNumber
  useEffect(() => {
    setJumpPage(pageNumber);
  }, [pageNumber]);

  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) {
      setPageNumber(p);
    } else {
      setJumpPage(pageNumber); // Reset to current if invalid
    }
  };

  // Scorecard Stats Calculation
  const handleShowAll = () => {
    setSearchText('');
    setFromDate(formatDateForPayload(new Date(new Date().getFullYear(), 0, 1)));
    setToDate(formatDateForPayload(new Date()));
    setFilters({
      bl_Lifecycle: true, bl_Appointment: true, bl_Docs: true, bl_Email: true,
      bl_Eshop: true, bl_LandingPage: true, bl_Notes: true, bl_SMS: true,
      bl_Support: true, bl_Task: true, bl_Voice_Calls: true, bl_WebForm: true
    });
    setAdvancedFilters(null);
    setActiveStat('All');
    setPageNumber(1);
  };

  const handleExport = async () => {
    try {
      toast.loading('Preparing export...', { id: 'export-loader' });

      const payload = {
        Token: TokenId,
        Details: {
          strFromDate: fromDate,
          strToDate: toDate,
          strLeadName: searchText,
          ParentId: parentId.toString(),
          strLeadID: "",
          strTimezone: "India Standard Time",
          ...filters
        }
      };

      const response = await fetch(`${process.env.REACT_APP_SERVICES_API_BASE_URL}${API_ENDPOINTS.LEAD_ACTIVITIES.EXPORT_LEAD_ACTIVITY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${TokenId}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.Details || [];

        if (data.length === 0) {
          toast.error('No data available to export', { id: 'export-loader' });
          return;
        }

        // Create workbook and worksheet
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Activities");

        // Generate file name with current date/time
        const fileName = `LeadActivityList_${new Date().toLocaleDateString().replace(/\//g, '-')}_${new Date().toLocaleTimeString().replace(/[:\s]/g, '')}.xlsx`;

        // Save file
        XLSX.writeFile(wb, fileName);
        toast.success('Export successful', { id: 'export-loader' });
      } else {
        toast.error('Export failed', { id: 'export-loader' });
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error('Export error occurred', { id: 'export-loader' });
    }
  };

  const statsData = useMemo(() => {
    // Calculate today's activities from the current list (Robust parsing)
    const today = new Date();
    const todayDay = today.getDate();
    const todayMonth = today.getMonth(); // 0-indexed
    const todayYear = today.getFullYear();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const todayCount = activities.filter(a => {
      if (!a.EventDate) return false;
      try {
        // Format: "11-May-2024"
        const parts = a.EventDate.split('-');
        if (parts.length !== 3) return false;

        const d = parseInt(parts[0]);
        const mStr = parts[1];
        const y = parseInt(parts[2]);

        const mIdx = monthNames.findIndex(mn => mn.toLowerCase() === mStr.toLowerCase());

        return d === todayDay && mIdx === todayMonth && y === todayYear;
      } catch (e) {
        return false;
      }
    }).length;

    return [
      {
        title: filteredActivities.length || 0,
        subtitle: 'Total Activities',
        icon: 'ri:list-unordered',
        color: 'primary',
        key: 'All'
      },
      {
        title: 'Top Records',
        subtitle: `${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)} (${Object.values(filters).filter(Boolean).length} types)`,
        icon: 'ri:filter-3-line',
        color: 'warning',
        key: 'Summary'
      }
    ];
  }, [activities, totalRecords, fromDate, toDate, filters]);

  const handleSelectRow = (activity) => {
    setViewingActivity(activity);
  };

  return (
    <div className="lead-activity-container" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', backgroundColor: '#f8fafc' }}>
      {!viewingLeadId && (
        <SubHeader
          pageName="Lead Activities"
          pageCode="PAG10037"
          title="Lead Activity"
          searchQuery={searchText}
          setSearchQuery={setSearchText}
          searchPlaceholder="Search activities..."
          viewMode={viewMode}
          setViewMode={setViewMode}
          showViewSwitcher={true}
          showGrid={false}
          showTable={true}
          showCalendar={true}
          showSearch={!!activityPerms?.Search}
          onSearchClick={activityPerms?.Search ? fetchActivities : null}
          onRefreshClick={fetchActivities}
          onSettingsClick={() => setShowViewOptions(!showViewOptions)}
          onFilterClick={activityPerms?.Search ? () => setIsFilterOpen(!isFilterOpen) : null} // Link to custom panel
          onExportClick={activityPerms?.Export ? handleExport : null}
          showFilter={!!activityPerms?.Search}
          showSettings={true}
          showExport={!!activityPerms?.Export}
          showAdvanceFilter={false} // Remove internal SubHeader filter button
          totalCount={activeStat === 'Today' ? todayActivitiesCount : filteredActivities.length}
          // Remove default advanced filters to use our custom panel
          advanceFilterFields={[]}
          onAdvanceFilterSubmit={null}
          filterCount={Object.values(filters).filter(f => !f).length > 0 ? 1 : 0}
        />
      )}

      {/* --- Restore Custom Filter Panel --- */}
      {isFilterOpen && !viewingLeadId && (
        <div className="absolute top-[72px] right-6 z-[1002] la-animate-slide-in">
          <div className="la-filter-panel shadow-2xl relative">
            {/* Close Icon */}
            <button
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setIsFilterOpen(false)}
            >
              <X size={20} />
            </button>

            <div className="la-filter-grid-2">
              <div className="la-filter-col">
                <label className="la-filter-label">From Date *</label>
                <div 
                  className="la-filter-input w-full flex items-center justify-between cursor-pointer bg-[#fdfdfd]"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="date"]');
                    if (input) input.showPicker ? input.showPicker() : input.click();
                  }}
                  style={{ minHeight: '44px' }}
                >
                  <span className="text-[13px] font-bold text-slate-700">{tempFromDate || 'Select Date'}</span>
                  <Calendar size={16} className="text-slate-400" />
                  <input
                    type="date"
                    className="absolute opacity-0 pointer-events-none w-0 h-0"
                    value={formatForDateInput(tempFromDate)}
                    onChange={(e) => setTempFromDate(formatDateForPayload(e.target.value))}
                  />
                </div>
              </div>
              <div className="la-filter-col">
                <label className="la-filter-label">To Date *</label>
                <div 
                  className="la-filter-input w-full flex items-center justify-between cursor-pointer bg-[#fdfdfd]"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="date"]');
                    if (input) input.showPicker ? input.showPicker() : input.click();
                  }}
                  style={{ minHeight: '44px' }}
                >
                  <span className="text-[13px] font-bold text-slate-700">{tempToDate || 'Select Date'}</span>
                  <Calendar size={16} className="text-slate-400" />
                  <input
                    type="date"
                    className="absolute opacity-0 pointer-events-none w-0 h-0"
                    value={formatForDateInput(tempToDate)}
                    onChange={(e) => setTempToDate(formatDateForPayload(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 mb-5 relative">
              <label className="text-[11px] font-bold text-slate-500 uppercase">Lead</label>
              <div className="la-search-input-wrapper relative">
                <input
                  type="text"
                  className="la-filter-input"
                  placeholder="Search lead..."
                  value={tempLeadSearchText}
                  onChange={(e) => {
                    setTempLeadSearchText(e.target.value);
                    if (tempSelectedLeadId) setTempSelectedLeadId('');
                  }}
                />
                {/* Suggestions logic should ideally use tempLeadSearchText too */}
                {leadSuggestions.length > 0 && (
                  <div className="la-suggestions-dropdown shadow-2xl la-animate-fade-in">
                    {leadSuggestions.map((lead, idx) => (
                      <div
                        key={lead.Id || idx}
                        className="la-suggestion-item"
                        onClick={() => {
                          setTempLeadSearchText(lead.Name);
                          setTempSelectedLeadId(lead.Id);
                          setLeadSuggestions([]);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="la-suggestion-avatar">
                            {lead.Image && lead.Image !== '/assets/custom/img/img_avatar1.png' ? (
                              <img src={lead.Image} alt="" className="w-full h-full object-cover rounded-full" />
                            ) : (
                              getInitials(lead.Name)
                            )}
                          </div>
                          <div className="flex flex-col min-width-0">
                            <span className="font-bold text-slate-700 text-sm">{lead.Name}</span>
                            <span className="text-[11px] text-slate-500 font-medium">{lead.MobileNo}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-6">
                  <span className="la-filter-section-title">Activity Type</span>
                  <div className="flex items-center gap-4 bg-slate-50 p-1 rounded-lg border border-slate-100">
                    <label className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-white transition-colors">
                      <input 
                        type="radio" 
                        name="tempFilterLogic" 
                        value="OR" 
                        checked={tempFilterLogic === 'OR'} 
                        onChange={() => setTempFilterLogic('OR')}
                        className="w-3 h-3 text-[#666cff]"
                      />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${tempFilterLogic === 'OR' ? 'text-[#666cff]' : 'text-slate-400'}`}>Any</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-white transition-colors">
                      <input 
                        type="radio" 
                        name="tempFilterLogic" 
                        value="AND" 
                        checked={tempFilterLogic === 'AND'} 
                        onChange={() => setTempFilterLogic('AND')}
                        className="w-3 h-3 text-[#666cff]"
                      />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${tempFilterLogic === 'AND' ? 'text-[#666cff]' : 'text-slate-400'}`}>All</span>
                    </label>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="la-checkbox"
                    checked={Object.values(tempFilters).every(Boolean)}
                    onChange={(e) => handleTempFilterSelectAll(e.target.checked)}
                  />
                  <span className="text-xs font-bold text-slate-500 uppercase">Select All</span>
                </label>
              </div>


              <div className="la-filter-grid text-[12px]">
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Lifecycle} onChange={() => toggleTempFilter('bl_Lifecycle')} />
                  <span>Lifecycle Events</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Email} onChange={() => toggleTempFilter('bl_Email')} />
                  <span>Email</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_SMS} onChange={() => toggleTempFilter('bl_SMS')} />
                  <span>SMS</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Voice_Calls} onChange={() => toggleTempFilter('bl_Voice_Calls')} />
                  <span>Voice & Calls</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Eshop} onChange={() => toggleTempFilter('bl_Eshop')} />
                  <span>E-Shop</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Support} onChange={() => toggleTempFilter('bl_Support')} />
                  <span>Support</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Task} onChange={() => toggleTempFilter('bl_Task')} />
                  <span>Task</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Appointment} onChange={() => toggleTempFilter('bl_Appointment')} />
                  <span>Appointment</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Docs} onChange={() => toggleTempFilter('bl_Docs')} />
                  <span>Docs</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_Notes} onChange={() => toggleTempFilter('bl_Notes')} />
                  <span>Notes</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_LandingPage} onChange={() => toggleTempFilter('bl_LandingPage')} />
                  <span>Landing Page</span>
                </label>
                <label className="la-filter-checkbox-row">
                  <input type="checkbox" className="la-checkbox" checked={tempFilters.bl_WebForm} onChange={() => toggleTempFilter('bl_WebForm')} />
                  <span>WebForm</span>
                </label>
              </div>
            </div>

            <div className="la-filter-footer">
              <button
                className="la-apply-btn"
                onClick={() => {
                  setFromDate(tempFromDate);
                  setToDate(tempToDate);
                  setFilters({ ...tempFilters });
                  setLeadSearchText(tempLeadSearchText);
                  setSelectedLeadId(tempSelectedLeadId);
                  setFilterLogic(tempFilterLogic);
                  setIsFilterOpen(false);
                  setPageNumber(1);

                  // The useEffect will trigger fetchActivities naturally when these states update
                }}
              >
                Apply
              </button>
            </div>

          </div>
        </div>
      )}

      {/* --- Scorecards Row (Standardized placement below header) --- */}
      {!viewingLeadId && (viewMode === 'table' || viewMode === 'calendar') && (
        <div style={{ width: '100%', marginBottom: '0px', flexShrink: 0, position: 'relative', zIndex: 99, overflow: 'visible' }}>
          <PremiumModuleCards
            customCards={statsData}
            onStatusClick={handleStatusClick}
            moduleName="Activity"
            activeFilters={[activeStat]}
            showToggle={true}
          />
        </div>
      )}

      {/* Floating Operations Toolbar (Settings Menu) */}
      {showViewOptions && !viewingLeadId && (
        <div className="absolute top-[72px] right-6 z-[1001] la-animate-slide-in">
          <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[200px]">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Module Settings</span>
            </div>
            <div className="p-2 flex flex-col gap-1">
              <div className="px-3 py-2 text-xs text-slate-400 italic">No additional settings available</div>
            </div>
          </div>
        </div>
      )}

      <div className="la-content-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full"><Loader /></div>
        ) : activities.length > 0 ? (
          viewMode === 'table' ? (
            <div className="flex-1 w-full relative overflow-hidden flex flex-col">
              <div className="flex-1 overflow-auto">
                <LeadActivityListTable
                  data={(activeStat === 'Today' ? todayActivities : sortedActivities).map(a => maskObject(a))}
                  loading={false}
                  totalRecords={activeStat === 'Today' ? todayActivitiesCount : totalRecords}
                  currentPage={pageNumber}
                  onPageChange={(page) => handlePageChange(page)}
                  itemsPerPage={pageSize}
                  onItemsPerPageChange={(size) => {
                    setPageSize(size);
                    setPageNumber(1);
                  }}
                  onViewActivity={handleSelectRow}
                  onViewLead={setViewingLeadId}
                  sortConfig={sortConfig}
                  onSortChange={handleSort}
                  isHiddenField={isHiddenField}
                />
              </div>
            </div>
          ) : viewMode === 'calendar' ? (
            <div className="la-calendar-view scrollbar-thin overflow-auto" style={{ flex: 1, background: '#fff' }}>
              <DynamicCalendar
                events={calendarEvents}
                initialView="month"
                initialDate={calendarInitialDate}
                entityLabel="lead activity"
                onEventClick={(event) => {
                  // Whole card click can still trigger something or we can leave it
                  // If we want specific links inside, we handle propagation
                }}
                eventRenderer={(event) => {
                  const act = event.extendedProps;
                  const personStyle = getAvatarStyle(act.PersonName);
                  const userStyle = getAvatarStyle(act.UserName);

                  return (
                    <div className={`la-cal-event ${event.status}`}>
                      <div 
                        className="la-cal-event-title" 
                        style={{ fontSize: '11px', fontWeight: 800, color: '#666cff', marginBottom: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingActivity(act);
                        }}
                      >
                        {act.EventName || 'New Interaction added'}
                      </div>

                      <div className="la-cal-event-participants">
                        <div className="la-participant">
                          <div 
                            className="la-avatar-circle pill-shadow"
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: personStyle.bg,
                              color: personStyle.color,
                              fontSize: '8px',
                              fontWeight: 800,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {getInitials(act.PersonName)}
                          </div>
                          <span 
                            className="la-p-name la-name-link" 
                            style={{ fontSize: '10px', textDecoration: 'underline' }}

                            onClick={(e) => {
                              e.stopPropagation();
                              if (act.LeadID) setViewingLeadId(act.LeadID);
                            }}
                          >
                            {act.PersonName}
                          </span>
                        </div>

                        <div className="la-participant">
                          <div 
                            className="la-avatar-circle pill-shadow"
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: '#f1f5f9',
                              color: '#64748b',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              border: '1px solid #e2e8f0'
                            }}
                          >
                            <User size={12} strokeWidth={2.5} />
                          </div>
                          <span className="la-p-name" style={{ fontSize: '10px' }}>{act.UserName}</span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          ) : (
            <div className="la-empty-state">
              <img src={nodata} alt="No Data" />
              <p>No activities found for the selected filters.</p>
            </div>
          )
        ) : (
          <div className="la-empty-state">
            <img src={nodata} alt="No Data" />
            <p>No activities found for the selected filters.</p>
          </div>
        )}
      </div>

      {/* Main Detail View */}
      {viewingLeadId && (
        <div className="lead-detail-fullscreen-overlay">
          <LeadDetails
            leadId={viewingLeadId}
            onBack={() => setViewingLeadId(null)}
            module="activities"
            maskObject={maskObject}
            isHiddenField={isHiddenField}
          />
        </div>
      )}

      {/* Activity Detail Side Drawer */}
      {viewingActivity && (
        <div className="la-detail-drawer-overlay" onClick={() => setViewingActivity(null)}>
          <div className="la-detail-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="la-drawer-header">
              <button className="la-back-to-list" onClick={() => setViewingActivity(null)}>
                <X size={20} />
                <span>Back to Lead Activity</span>
              </button>
            </div>

            <div className="la-drawer-body">
              <div className="la-detail-card-wrapper">
                <div className="la-detail-card shadow-sm">
                  <div className="la-card-title-v2">Lead Activity</div>

                  <div className="la-lead-profile-section">
                    <div
                      className="la-detail-avatar-v2"
                      style={{
                        backgroundColor: getAvatarStyle(viewingActivity.PersonName).bg,
                        color: getAvatarStyle(viewingActivity.PersonName).color
                      }}
                    >
                      {getInitials(viewingActivity.PersonName)}
                    </div>
                    <div className="la-detail-lead-name">
                      {viewingActivity.PersonName || viewingActivity.ContactName || 'N/A'}
                    </div>
                  </div>

                  <div className="la-info-grid">
                    <div className="la-info-row">
                      <span className="la-info-label">Activity Title:</span>
                      <div className="la-info-value-with-icon">
                        <img
                          src={getRemoteIcon(viewingActivity.EventName)}
                          alt=""
                          className="la-detail-act-icon"
                          onError={(e) => { e.target.src = "https://kit19.com/assets/custom/UserCRMCampaign/Images/LeadActivityIMG/lifecycle.png" }}
                        />
                        <span className="la-info-value-bold">
                          {viewingActivity.EventName || viewingActivity.EventTitle || viewingActivity.ActivityTitle || 'Activity'}
                        </span>
                      </div>
                    </div>

                    <div className="la-info-row">
                      <span className="la-info-label">Activity Description:</span>
                      <span className="la-info-value">
                        {viewingActivity.EventDescription || viewingActivity.ActivityDescription || 'N/A'}
                      </span>
                    </div>

                    <div className="la-info-row">
                      <span className="la-info-label">Created Date:</span>
                      <span className="la-info-value">
                        {viewingActivity.EventDate} {viewingActivity.EventTime}
                      </span>
                    </div>

                    <div className="la-info-row">
                      <span className="la-info-label">Created By:</span>
                      <span className="la-info-value">
                        {viewingActivity.UserName || viewingActivity.CreatedBy || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadActivityModule;
